/**
 * AnalysisEngine — métricas técnicas para ajudar o cantor.
 *
 * Regras de honestidade (definidas no produto):
 *  - Nunca julgar qualidade artística.
 *  - Dizer o que foi medido de fato (`method`).
 *  - No modo YouTube NÃO temos o áudio da base, então não prometemos comparar
 *    afinação com a música — medimos estabilidade, nível, clipping, timing e
 *    respiração, e informamos que a comparação com a base exige o
 *    instrumental autorizado.
 */
import type { AnalysisIssue, PitchFrame, VocalAnalysis } from '../types.js';
import {
  crestFactor,
  findClipping,
  findSilence,
  detectOnsets,
  peak,
  rms,
  rmsWindows,
} from './analysis.js';
import { analyzePitch, estimateKey, pitchStabilityScore } from './pitch.js';
import { clamp, formatTime, freqToNote, gainToDb, median } from '../utils/misc.js';

export interface AnalyzeInput {
  vocal: Float32Array;
  sampleRate: number;
  instrumental?: Float32Array | null;
  instrumentalSampleRate?: number;
  /** Deslocamento manual atual (ms) — para o comentário de sincronia. */
  syncOffsetMs?: number;
  hasReferenceAudio: boolean;
  trackName?: string;
  /** Duração máxima analisada (para não travar celulares em takes longos). */
  maxSeconds?: number;
}

export class AnalysisEngine {
  analyze(input: AnalyzeInput): VocalAnalysis {
    const sr = input.sampleRate;
    const maxSamples = Math.round((input.maxSeconds ?? 240) * sr);
    const vocal = input.vocal.length > maxSamples ? input.vocal.subarray(0, maxSamples) : input.vocal;
    const durationSec = vocal.length / sr;

    const p = peak(vocal);
    const r = rms(vocal);
    const clipping = findClipping(vocal, sr, 0.995, 4);
    const silences = findSilence(vocal, sr, -50, 400);
    const onsets = detectOnsets(vocal, sr, { minIntervalMs: 120 });
    const windows = rmsWindows(vocal, sr, 400, 200);
    const pitchFrames = analyzePitch(vocal, sr, { windowMs: 50, hopMs: 25, threshold: 0.62 });
    const stability = pitchStabilityScore(pitchFrames);
    const detectedKey = estimateKey(pitchFrames);

    const issues: AnalysisIssue[] = [];
    const method: string[] = [
      'Pico e RMS medidos amostra a amostra.',
      'Clipping: amostras ≥ 0,995 por ≥ 4 amostras seguidas.',
      'Afinação (estabilidade): pitch por NSDF + desvio típico em cents.',
      'Ritmo: variabilidade dos intervalos entre ataques detectados.',
      'Nível: RMS da voz comparado ao da base quando a base está disponível.',
    ];

    // ---------- clipping ----------
    if (clipping.length) {
      const first = clipping[0];
      issues.push({
        id: 'clipping',
        severity: 'critical',
        message: `Há distorção (clipping) entre ${formatTime(first.start)} e ${formatTime(first.end)}. Afaste um pouco o microfone ou reduza o ganho de entrada e regreve o trecho.`,
        time: first.start,
        endTime: first.end,
      });
      method.push(`Clipping encontrado em ${clipping.length} região(ões).`);
    }

    // ---------- nível ----------
    const vocalDb = gainToDb(r);
    let levelScore = 60;
    if (input.instrumental && input.instrumental.length) {
      const isr = input.instrumentalSampleRate ?? sr;
      const instDb = gainToDb(rms(input.instrumental));
      const diff = vocalDb - instDb;
      levelScore = clamp(Math.round(100 - Math.abs(diff - 3) * 9), 0, 100);
      if (diff < -6) {
        issues.push({
          id: 'level-low',
          severity: 'warn',
          message: `A voz está ${Math.abs(Math.round(diff))} dB abaixo da base. Use "ajustar volume automaticamente" ou suba o fader da voz.`,
        });
      } else if (diff > 12) {
        issues.push({
          id: 'level-high',
          severity: 'info',
          message: 'A voz está bem à frente da base. Se quiser mais "cola", abaixe um pouco a voz ou suba a base.',
        });
      } else {
        issues.push({
          id: 'level-ok',
          severity: 'info',
          message: `Bom equilíbrio: voz ${diff >= 0 ? '+' : '−'}${Math.abs(Math.round(diff))} dB em relação à base.`,
        });
      }
      method.push('Nível comparado com a base enviada (RMS).');
    } else {
      levelScore = clamp(Math.round(100 - Math.abs(vocalDb + 20) * 4), 20, 100);
      if (!input.hasReferenceAudio) {
        issues.push({
          id: 'no-reference',
          severity: 'info',
          message:
            'Sem o áudio da base não comparamos níveis nem afinação com a música. Para isso, envie o instrumental que você tem direito de usar.',
        });
        method.push('Modo YouTube: sem áudio da base (o YouTube não pode ser extraído).');
      }
    }

    // ---------- estabilidade de afinação ----------
    const pitchScore = stability.score;
    if (stability.voicedRatio < 0.15 && durationSec > 4) {
      issues.push({
        id: 'pitch-few',
        severity: 'info',
        message: 'Detectamos pouca nota sustentada — normal em trechos falados/rap. A métrica de afinação fica menos relevante aqui.',
      });
    } else if (pitchScore > 0 && pitchScore < 55) {
      issues.push({
        id: 'pitch-var',
        severity: 'info',
        message: `Variação média de ${Math.round(stability.medianAbsCents)} cents entre as notas. Se quiser mais precisão, cante mais perto do microfone e sustente as notas finais.`,
      });
    } else if (pitchScore >= 80) {
      issues.push({
        id: 'pitch-good',
        severity: 'info',
        message: 'Notas bem estáveis nesta gravação.',
      });
    }

    // ---------- timing ----------
    const timingScore = computeTimingScore(onsets.times);
    if (onsets.times.length >= 6) {
      method.push(`Ritmo calculado sobre ${onsets.times.length} ataques detectados.`);
      if (timingScore < 50) {
        issues.push({
          id: 'timing-var',
          severity: 'info',
          message: 'Os ataques variaram bastante de intervalo. Se a intenção for um trecho ritmado, gravar com metrônomo ajuda.',
        });
      }
    } else {
      method.push('Poucos ataques detectados: métrica de ritmo não aplicada.');
    }

    // ---------- entrada no tempo ----------
    const firstOnset = onsets.times[0] ?? null;
    if (firstOnset != null) {
      const late = firstOnset > 1.2;
      issues.push({
        id: 'entry',
        severity: 'info',
        message: late
          ? `Sua primeira entrada aparece em ${formatTime(firstOnset)} — um pouco depois do início da gravação.`
          : `Entrada no tempo: você começou em ${formatTime(firstOnset)}.`,
        time: firstOnset,
      });
    }

    // ---------- respiração ----------
    const longSil = silences.filter((s) => s.end - s.start > 2.5);
    const breath: VocalAnalysis['metrics']['breath'] =
      silences.length === 0 ? 'estavel' : longSil.length > 2 ? 'irregular' : 'estavel';
    if (longSil.length) {
      issues.push({
        id: 'silence',
        severity: 'info',
        message: `${longSil.length} pausa(s) longa(s) (a maior em ${formatTime(
          longSil.reduce((a, b) => (b.end - b.start > a.end - a.start ? b : a)).start
        )}). Nada errado: só avisando para você saber onde cortar se quiser.`,
        time: longSil[0].start,
      });
    }

    // ---------- dinâmica ----------
    const cf = crestFactor(vocal);
    const levelValues = windows.values.filter((v) => v > 1e-4).map((v) => gainToDb(v));
    const spread = levelValues.length > 4 ? Math.max(...levelValues) - Math.min(...levelValues) : 0;
    const dynamicsScore = clamp(Math.round(100 - Math.abs(spread - 14) * 3.5), 0, 100);
    if (spread > 22) {
      issues.push({
        id: 'dynamics',
        severity: 'info',
        message: `A diferença entre as partes mais fracas e mais fortes foi de ${Math.round(spread)} dB. Um compressor (preset STUDIO ou POP) ajuda a uniformizar.`,
      });
    }
    method.push(`Fator de crista: ${cf.toFixed(1)} dB; variação de nível: ${Math.round(spread)} dB.`);

    // ---------- sincronia ----------
    const off = input.syncOffsetMs ?? 0;
    if (Math.abs(off) > 120) {
      issues.push({
        id: 'sync',
        severity: 'warn',
        message: `O ajuste de sincronização está em ${off > 0 ? '+' : '−'}${Math.abs(Math.round(off))} ms. Se a voz parecer atrasada/adiantada, use "adiantar/atrasar voz" em passos de 10 ms.`,
      });
    }

    // ---------- pitch visual (frames reduzidos para a UI) ----------
    const frames: PitchFrame[] = pitchFrames
      .filter((f, i) => i % 2 === 0)
      .slice(0, 1200)
      .map((f) => ({
        time: f.time,
        freq: f.freq,
        confidence: f.confidence,
        note: f.note,
        cents: f.cents,
        rms: f.rms,
      }));

    const noteHistogram = stability.notes.slice(0, 12);
    const midis = pitchFrames.filter((f) => f.freq).map((f) => freqToNote(f.freq!)!.midi);
    const rangeSemitones = midis.length ? Math.max(...midis) - Math.min(...midis) : undefined;

    return {
      createdAt: Date.now(),
      durationSec,
      metrics: {
        pitchStability: pitchScore,
        timing: timingScore,
        level: levelScore,
        dynamics: dynamicsScore,
        breath,
      },
      pitch: {
        frames,
        detectedKey,
        noteHistogram,
        rangeSemitones,
      },
      issues,
      notes: buildCoachNotes({
        peak: p,
        rmsDb: vocalDb,
        pitchScore,
        timingScore,
        levelScore,
        clipping: clipping.length,
        detectedKey,
        rangeSemitones,
      }),
      method,
      hasReference: input.hasReferenceAudio,
    };
  }

  /** Pitch em tempo real (para o medidor de nota durante a gravação). */
  realtimePitch(data: Float32Array, sampleRate: number): { note?: string; cents?: number; freq: number | null } {
    const frames = analyzePitch(data, sampleRate, { windowMs: 45, hopMs: 45, threshold: 0.6 });
    const voiced = frames.filter((f) => f.freq);
    if (!voiced.length) return { freq: null };
    const last = voiced[voiced.length - 1];
    return { freq: last.freq, note: last.note, cents: last.cents };
  }
}

function computeTimingScore(onsets: number[]): number {
  if (onsets.length < 6) return 0;
  const intervals: number[] = [];
  for (let i = 1; i < onsets.length; i++) intervals.push(onsets[i] - onsets[i - 1]);
  const med = median(intervals);
  if (med <= 0) return 0;
  const dev = intervals.map((i) => Math.abs(i - med) / med);
  const rel = median(dev);
  return clamp(Math.round(100 * (1 - rel * 2.6)), 0, 100);
}

function buildCoachNotes(x: {
  peak: number;
  rmsDb: number;
  pitchScore: number;
  timingScore: number;
  levelScore: number;
  clipping: number;
  detectedKey?: string;
  rangeSemitones?: number;
}): string[] {
  const notes: string[] = [];
  if (x.clipping) notes.push('Grave o trecho novamente com um pouco menos de ganho — houve distorção.');
  if (x.rmsDb < -34) notes.push('A voz ficou baixa. Aproxime o microfone (~15 cm) e cante no volume normal.');
  if (x.rmsDb > -12) notes.push('A voz ficou bem alta: deixe uns 6 dB de folga para os refrões.');
  if (x.pitchScore >= 80) notes.push('Boa estabilidade de notas ao longo do take.');
  if (x.timingScore >= 80) notes.push('Entradas ritmicamente consistentes.');
  if (x.detectedKey) notes.push(`Notas mais presentes sugerem algo próximo de ${x.detectedKey} (estimativa, não análise harmônica completa).`);
  if (x.rangeSemitones != null && x.rangeSemitones > 18)
    notes.push(`Você usou uma extensão de ~${x.rangeSemitones} semitons nesta gravação.`);
  if (!notes.length) notes.push('Take utilizável: ajuste o volume da voz e escolha um preset de efeitos antes de exportar.');
  return notes;
}

export const analysisEngine = new AnalysisEngine();
